import {
  BitrixEmailTriesResponse,
  bitrixEmailService,
} from '@/app/modules/bitrix/BitrixEmailService';
import { handleRequestError } from '@/shared/api/handleApiError';
import { useViewAsOwner } from '@/shared/composition';
import { useSentryAsync } from '@/shared/composition/useSentryAsync';
import { BitrixClient } from '@/shared/lib/types/bitrix';
import { axiosBitrix } from '@/shared/plugins/axiosBitrix';
import { bitrixEmailChange } from '@/shared/store/bitrixEmailChange';
import { useUiStore } from '@/shared/store/useUiStore';
import { debug } from 'debug';
import moment from 'moment';
import { defineStore } from 'pinia';
import { nextTick } from 'vue';

const { captureExceptionSentry } = useSentryAsync();
const localDebug = debug('app:pinia:bitrixStore');

const handleBitrixUserResponse = (bitrixResponse: any) => {
  const client: any = Object.values(bitrixResponse).reduce((acc: any, item) => {
    if (item !== null) {
      acc = Object.assign(acc, item);
    }
    return acc;
  }, {});

  const isConfirmed = client.is_client_email_confirmed !== '0' && !!client.is_client_email_confirmed;
  return {
    ...client,
    isNewClient: bitrixResponse.client === null,
    isCurrentClient: bitrixResponse.client !== null,
    isOldClient: bitrixResponse.old_email !== null,
    isChangeEmailConfirmed: isConfirmed,
    // Только id берем от текущего клиента
    id: bitrixResponse.client === null ? null : bitrixResponse.client.id,
  };
};

export const useBitrixStore = defineStore('bitrix', {
  state: () => ({
    changeEmail: {
      is_client_email_confirmed: false as boolean,
      current_attempts: 0 as number,
      total_attempts: 0 as number,
      time_limit: 0 as number,
      deadline: '',
    } as BitrixEmailTriesResponse,
    client: {
      id: null,
      name: null as null | string,
      surname: null as null | string,
      patronymic: null as null | string,
      birth_date: null as null | string,
      sex: null as null | string,
      partner_id: null as null | number,
      phone: null as null | string,
      akada_fl_guid: null as null | string,
      bitrix_id: null as null | string,
      email: null as string | null,
      change_client_email: null as string | null,
      change_b24_email: null as string | null,
      old_emails: false as any[] | boolean,
      change_client_phone: null,
      change_b24_phone: null,
      old_phones: false,
      change_email_code: null,
      is_client_email_confirmed: false,
      changeEmailMessage: {
        text: '',
        title: '',
        info: '',
        isError: false,
        canCancel: false,
        canRepeat: false,
        canResend: false,
      },
      is_change_email: false,
      is_old_email: false,
      isNewClient: false,
      isCurrentClient: false,
      isOldClient: false,
      isChangeEmailConfirmed: false,
      isReplaceEmailCompleted: false,
      updated_terms: null,
    },
  }),
  actions: {
    async replaceEmail() {
      localDebug('replace email');
      try {
        const data = await bitrixEmailService.replaceEmail();
        const bitrixUser: any = handleBitrixUserResponse({
          client: data,
          change_client_email: null,
          change_b24_email: null,
          old_email: null,
        });
        if (bitrixUser.id) {
          bitrixEmailChange.showCompleteEmailChange.value = true;
        }
        this.setReplaceEmailCompleted(!!bitrixUser.id);
        this.setClient(bitrixUser);
      } catch {
        bitrixEmailChange.isReplaceEmailError.value = true;
      }
    },
    async fetchClientInformation() {
      localDebug('fetch info from bus');
      const { ownerEmail } = useViewAsOwner();
      try {
        let getUrl = '/api/user/get';
        if (ownerEmail.value) {
          getUrl += '?email=' + ownerEmail.value;
        }
        const data = await axiosBitrix.get(getUrl);
        const bitrixUser: any = handleBitrixUserResponse(data.data);

        if (!Object.keys(bitrixUser).length) {
          await this.createClient();
        } else {
          this.setClient(bitrixUser);

          nextTick(() => {
            if (this.emailChangeProps.needReplace) {
              this.replaceEmail();
            }
          });
        }
      } catch (error: any) {
        if (error?.response?.status === 404 && !ownerEmail.value) {
          await this.createClient();
          return;
        }
        handleRequestError(error);
      }
    },
    async createClient() {
      localDebug('create bus client');
      try {
        const url = '/api/user/add';
        const data = await axiosBitrix.post(url);

        this.client = data.data;
      } catch (error: any) {
        captureExceptionSentry(error, 'Создание КИ битрикс');
        // Без пользователя в БУС кабинет работать не может - блокируем его
        const uiStore = useUiStore();
        uiStore.enableUserCreateError({
          code: error?.response?.data?.errorCode ?? error?.response?.status ?? 0,
          time: moment().format('DD.MM.YYYY HH:mm:ss'),
          // В тексте ошибки БУС указан имейл пользователя
          message: error?.response?.data?.error ?? '',
        });
      }
    },
    async updateClient(payload: Partial<BitrixClient>, withHeader = false) {
      try {
        return (await axiosBitrix.post('/api/user/update', payload, withHeader ? { headers: { 'event-type': 1 } } : {}))
          .data;
      } catch (error) {
        captureExceptionSentry(error, 'Обновление КИ битрикс');
      }
    },
    async updatePhone(payload: string) {
      localDebug('update phone in bus');
      try {
        await axiosBitrix.post(
          '/api/user/update',
          {
            phone: payload,
          },
          {
            headers: {
              'event-type': 2,
            },
          },
        );
      } catch (error) {
        captureExceptionSentry(error, 'Обновление КИ битрикс, только телефон');
      }
    },
    async updatePartnerId(payload: any) {
      try {
        await axiosBitrix.post('/api/user/update', {
          partner_id: payload,
        });
      } catch (error) {
        captureExceptionSentry(error, 'Обновление КИ битрикс, только partner_id');
      }
    },
    async updateBitrixId(payload: any) {
      try {
        await axiosBitrix.post('/api/user/update', {
          bitrix_id: payload,
        });
      } catch (error) {
        captureExceptionSentry(error, 'Обновление КИ битрикс, только битрикс id');
      }
    },
    // mutations
    setPhone(payload: string) {
      this.client.phone = payload;
    },
    setChangeEmail(payload: any) {
      this.changeEmail = Object.assign({ ...this.changeEmail }, payload);
    },
    setClientField(payload: any) {
      const [field, value] = payload;
      (this.client as any)[field] = value;
    },
    setChangeEmailMessage(payload: any) {
      this.client.changeEmailMessage = payload;
    },
    setReplaceEmailCompleted(payload: any) {
      this.client.isReplaceEmailCompleted = payload;
    },
    setClient(payload: any) {
      this.client = Object.assign({ ...this.client }, payload);
    },
    setBitrixId(payload: string) {
      this.client.bitrix_id = payload;
    },
    setPartnerId(payload: number) {
      this.client.partner_id = payload;
    },
  },
  getters: {
    phone(state) {
      return state.client.phone;
    },
    partnerId(state) {
      return state.client.partner_id;
    },
    bitrixId(state) {
      return state.client.bitrix_id;
    },
    clientId(state) {
      return state.client.id;
    },
    changeClientEmail(state) {
      return state.client.change_client_email;
    },
    clientEmail(state) {
      return state.client.email;
    },
    changeEmailCode(state) {
      return state.client.change_email_code;
    },
    oldEmails(state): any[] {
      return (state.client.old_emails || []) as any[];
    },
    emailChangeProps() {
      return Object.fromEntries(Object.entries(bitrixEmailChange).map(([key, value]) => [key, value.value]));
    },
  },
});
